import { useState } from "react";
import { motion } from "framer-motion";
import { Flame, Users, Video, ArrowLeft } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function CreatorForm() {
  const [form, setForm] = useState({ name: "", email: "", platform: "tiktok", handle: "", audience: "" });
  const [done, setDone] = useState("");
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const { data } = await axios.post(`${API}/community/apply`, form);
      setDone(data.message);
    } catch (err) {
      setError(err.response?.data?.detail || "Something went wrong — try again");
    }
  };
  if (done) return <p className="text-sm text-emerald-400 py-6" data-testid="creator-success">{done}</p>;
  return (
    <form onSubmit={submit} className="space-y-3" data-testid="creator-form">
      <div className="grid sm:grid-cols-2 gap-3">
        <input required placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="creator-name"
          className="bg-white/5 border border-white/15 rounded-lg px-4 py-3 text-sm outline-none focus:border-[#f97316] placeholder:text-slate-600" />
        <input required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="creator-email"
          className="bg-white/5 border border-white/15 rounded-lg px-4 py-3 text-sm outline-none focus:border-[#f97316] placeholder:text-slate-600" />
      </div>
      <div className="grid sm:grid-cols-3 gap-3">
        <select value={form.platform} onChange={(e) => setForm({ ...form, platform: e.target.value })} data-testid="creator-platform"
          className="bg-white/5 border border-white/15 rounded-lg px-4 py-3 text-sm outline-none">
          <option value="tiktok">TikTok</option>
          <option value="instagram">Instagram</option>
          <option value="youtube">YouTube</option>
          <option value="other">Other</option>
        </select>
        <input required placeholder="@handle" value={form.handle} onChange={(e) => setForm({ ...form, handle: e.target.value })} data-testid="creator-handle"
          className="bg-white/5 border border-white/15 rounded-lg px-4 py-3 text-sm outline-none focus:border-[#f97316] placeholder:text-slate-600" />
        <input placeholder="Audience size" value={form.audience} onChange={(e) => setForm({ ...form, audience: e.target.value })} data-testid="creator-audience"
          className="bg-white/5 border border-white/15 rounded-lg px-4 py-3 text-sm outline-none focus:border-[#f97316] placeholder:text-slate-600" />
      </div>
      {error && <p className="text-xs text-red-400" data-testid="creator-error">{error}</p>}
      <button type="submit" data-testid="creator-submit"
        className="font-display font-bold tracking-[0.12em] text-sm px-8 py-3.5 rounded-full bg-gradient-to-r from-[#f97316] to-[#ea580c] text-white shadow-[0_8px_40px_rgba(255,98,31,0.4)] hover:shadow-[0_8px_56px_rgba(255,98,31,0.65)] transition-shadow">
        APPLY AS CREATOR
      </button>
    </form>
  );
}

function UgcForm() {
  const [form, setForm] = useState({ name: "", email: "", url: "", description: "", permission: false });
  const [done, setDone] = useState("");
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const { data } = await axios.post(`${API}/community/ugc`, form);
      setDone(data.message);
    } catch (err) {
      setError(err.response?.data?.detail || "Something went wrong — try again");
    }
  };
  if (done) return <p className="text-sm text-emerald-400 py-6" data-testid="ugc-success">{done}</p>;
  return (
    <form onSubmit={submit} className="space-y-3" data-testid="ugc-form">
      <div className="grid sm:grid-cols-2 gap-3">
        <input required placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="ugc-name"
          className="bg-white/5 border border-white/15 rounded-lg px-4 py-3 text-sm outline-none focus:border-[#f97316] placeholder:text-slate-600" />
        <input required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="ugc-email"
          className="bg-white/5 border border-white/15 rounded-lg px-4 py-3 text-sm outline-none focus:border-[#f97316] placeholder:text-slate-600" />
      </div>
      <input required type="url" placeholder="Link to your video/photo" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} data-testid="ugc-url"
        className="w-full bg-white/5 border border-white/15 rounded-lg px-4 py-3 text-sm outline-none focus:border-[#f97316] placeholder:text-slate-600" />
      <textarea placeholder="Tell us about it…" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} data-testid="ugc-description"
        className="w-full bg-white/5 border border-white/15 rounded-lg px-4 py-3 text-sm outline-none focus:border-[#f97316] placeholder:text-slate-600" />
      <label className="flex items-start gap-3 text-xs text-slate-400 cursor-pointer">
        <input type="checkbox" checked={form.permission} onChange={(e) => setForm({ ...form, permission: e.target.checked })} data-testid="ugc-permission"
          className="accent-[#f97316] w-4 h-4 mt-0.5" />
        I give NOXWAY permission to feature this content on its website and social channels.
      </label>
      {error && <p className="text-xs text-red-400" data-testid="ugc-error">{error}</p>}
      <button type="submit" data-testid="ugc-submit"
        className="font-display font-bold tracking-[0.12em] text-sm px-8 py-3.5 rounded-full border border-[#f97316]/50 text-[#f97316] hover:bg-[#f97316] hover:text-black transition-colors">
        SUBMIT CONTENT
      </button>
    </form>
  );
}

export default function Community() {
  return (
    <div className="min-h-screen bg-[#08090d] text-white" data-testid="community-page">
      <div className="max-w-5xl mx-auto px-6 py-16 lg:py-24">
        <a href="/" className="inline-flex items-center gap-2 font-tech text-[10px] tracking-[0.2em] uppercase text-slate-400 hover:text-[#f97316] transition-colors mb-12" data-testid="community-back">
          <ArrowLeft size={13} /> Back to NOXWAY
        </a>
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}>
          <div className="font-tech text-[11px] tracking-[0.3em] text-[#f97316] uppercase mb-5">NOXWAY Community</div>
          <h1 className="font-display font-extrabold tracking-tight text-4xl sm:text-5xl mb-4" data-testid="community-headline">
            Create with the <span className="text-[#f97316]">fire line.</span>
          </h1>
          <p className="text-base sm:text-lg text-slate-400 max-w-2xl leading-relaxed mb-16">
            Join the NOXWAY creator program: get your own referral code, earn commission on attributed sales,
            and help communities prepare before wildfire season.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-8">
          <motion.div initial={{ opacity: 0, y: 32 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }}
            className="card-glow-border rounded-3xl p-8 bg-[#0f1117]" data-testid="creator-card">
            <Users size={22} className="text-[#f97316] mb-5" />
            <h2 className="font-display font-bold text-2xl mb-2">Creator program</h2>
            <p className="text-sm text-slate-400 mb-7">Approved creators receive a unique referral code with click, order and commission tracking.</p>
            <CreatorForm />
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 32 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.2 }}
            className="card-glow-border rounded-3xl p-8 bg-[#0f1117]" data-testid="ugc-card">
            <Video size={22} className="text-[#f97316] mb-5" />
            <h2 className="font-display font-bold text-2xl mb-2">Share your content</h2>
            <p className="text-sm text-slate-400 mb-7">Filmed NOXWAY in action or your wildfire prep? Submit it for a chance to be featured.</p>
            <UgcForm />
          </motion.div>
        </div>

        <div className="mt-16 flex items-center gap-3 text-slate-600">
          <Flame size={14} />
          <span className="font-tech text-[10px] tracking-[0.2em] uppercase">NOXWAY™ community — no fire crossing this way</span>
        </div>
      </div>
    </div>
  );
}
